import usePersistentStore, { useDesignSchemesBucket } from '@gui/store/persistent/persistentStore';
import { ControllerManager } from '@gui/controller/ControllerManager';
import LayerTree from '@gui/components/panels/data/LayerTree';
import SidePanelDialog from '@gui/components/panels/settings/SidePaneDialog';
import { Box, Button, Checkbox, MenuItem, Select, Slider, Stack, Typography } from '@mui/material';
import {
    DeepPartial,
    DesignSchemeSetType,
    FacadeComponentType,
    FacadeDirectionMode,
    FloorBasedDesignState,
    NotificationType,
    PipelinePhase,
    SIM_SUN4D,
    ShadingAutomaticRectangleReference,
    VerticalFacadeCoverageMode,
} from '@visualizer/types';
import { useEffect, useState } from 'react';
import { getGrid } from 'store/LoadingStore';
import { GroupLabel } from '../DesignSchemeView';
import Layer from 'Simulations/Layer';
import useNotificationStore from '@gui/store/notification';
import {
    CollapsibleGroupLabel,
    formatOneDecimal,
    optionalSliderLabelSx,
    sliderInteractionProps,
    sliderRowSx,
    sliderSx,
    sliderUnitSx,
    tinyLabelSx,
    tinySelectSx,
} from './FloorBasedView.shared';
import { addMixedLayerToCollection } from '../DesignSchemeViewUtils';

interface FloorBasedComponentsViewProps {
    setId: string;
    footprintId: string;
    schemeId: string;
    floorHeight: number;
    floorThickness: number;
    hideFacadesEnabled: boolean;
    componentSectionOpen: boolean;
    onToggle: () => void;
    updateFloorBased: (partial: DeepPartial<FloorBasedDesignState>) => void;
}

export const FloorBasedComponentsView: React.FC<FloorBasedComponentsViewProps> = ({
    setId,
    footprintId,
    schemeId,
    floorHeight,
    floorThickness,
    hideFacadesEnabled,
    componentSectionOpen,
    onToggle,
    updateFloorBased,
}) => {
    // Zustand Variables:
    const designSchemes = useDesignSchemesBucket((s) => s);
    const setType = designSchemes.sets?.[setId]?.type;
    const isFullSite = setType === DesignSchemeSetType.FULL_SITE;
    const scheme = designSchemes.sets?.[setId]?.footprints?.[footprintId]?.designSchemes?.[schemeId];
    const floorBasedState = scheme.floorBased;

    // Defaults:
    const defaults = designSchemes.defaults.floorBased;
    const { showNotification } = useNotificationStore.getState();
    const updateLayerState = usePersistentStore((s) => s.updateLayerState);
    const setSegmentsPanel = usePersistentStore((s) => s.setSegmentsPanel);
    const activeLayerID = usePersistentStore((s) => s.layers.activeLayerID);

    const applyLayerFrameSelection = (layerId: string, frameSelected: number) => {
        if (activeLayerID === layerId) {
            setSegmentsPanel({ isAggregate: false, frameSelected });
            ControllerManager.getInstance().getPipelineController().run();
            return;
        }
        updateLayerState(layerId, {
            segments: { isAggregate: false, frameSelected },
        });
        const grid = getGrid();
        const layer = grid?.searchLayerByUID(layerId);
        if (!layer) return;

        // Applies the correct frame selection:
        layer.getPipeline().clearCacheAll();
        const pipelineResult = layer.runPipeline({
            suppressNotifications: true,
            capturePhase: PipelinePhase.Regular,
            octTree: grid.octTree!,
        });
        ControllerManager.getInstance().getDesignSchemeController().refreshDesignSchemes(pipelineResult, layerId);
    };

    // Components Settings:
    const componentTypes = floorBasedState?.facadeState.componentTypes ?? defaults.facadeState.componentTypes;
    const componentLayerIds = floorBasedState?.facadeState.componentLayerIds ?? defaults.facadeState.componentLayerIds;
    const componentLink = floorBasedState?.facadeState.componentLink ?? defaults.facadeState.componentLink;
    const componentDirectionMode = floorBasedState?.facadeState.componentDirectionMode ?? defaults.facadeState.componentDirectionMode;
    const hasComponents = componentTypes.length > 0;
    const hasPvPanels = componentTypes.includes(FacadeComponentType.PV_PANELS);
    const hasShading = componentTypes.includes(FacadeComponentType.SHADING);
    const hasBalconies = componentTypes.includes(FacadeComponentType.BALCONIES);
    const hasGreen = componentTypes.includes(FacadeComponentType.GREEN);
    const [layerDialogOpen, setLayerDialogOpen] = useState(false);
    const [selectedComponentTypeForLayer, setSelectedComponentTypeForLayer] = useState<FacadeComponentType | null>(null);
    const [layerTreeRefresh, setLayerTreeRefresh] = useState(0);

    // PV Panel Settings:
    const verticalLength = floorBasedState?.facadeState.verticalLength?.value ?? defaults.facadeState.verticalLength.value;
    const verticalCoverageMode = floorBasedState?.facadeState.verticalCoverageMode ?? defaults.facadeState.verticalCoverageMode;
    const verticalConnectCorners = floorBasedState?.facadeState.verticalConnectCorners ?? defaults.facadeState.verticalConnectCorners;
    const shadingConnectCorners = floorBasedState?.facadeState.shadingConnectCorners ?? defaults.facadeState.shadingConnectCorners;
    const verticalClosed = floorBasedState?.facadeState.verticalClosed ?? defaults.facadeState.verticalClosed;
    const verticalHeight = floorBasedState?.facadeState.verticalHeight?.value ?? defaults.facadeState.verticalHeight.value;
    const verticalCustomAngle = floorBasedState?.facadeState.verticalCustomAngle ?? defaults.facadeState.verticalCustomAngle;
    const verticalAngle = floorBasedState?.facadeState.verticalAngle?.value ?? defaults.facadeState.verticalAngle.value;
    const pvOffset = floorBasedState?.facadeState.pvOffset?.value ?? defaults.facadeState.pvOffset.value;
    const verticalHeightB = floorBasedState?.facadeState.verticalHeightB ?? defaults.facadeState.verticalHeightB;

    // Balcony Settings:
    const balconyLength = floorBasedState?.facadeState.balconyLength?.value ?? defaults.facadeState.balconyLength.value;
    const balconyConnectCorners = floorBasedState?.facadeState.balconyConnectCorners ?? defaults.facadeState.balconyConnectCorners;
    const balconyOnlyOnOpenings = floorBasedState?.facadeState.balconyOnlyOnOpenings ?? defaults.facadeState.balconyOnlyOnOpenings;

    // Green Settings:
    const greenDepth = floorBasedState?.facadeState.greenDepth?.value ?? defaults.facadeState.greenDepth.value;

    // Shading Settings:
    const shadingDepth = floorBasedState?.facadeState.shadingDepth?.value ?? defaults.facadeState.shadingDepth.value;
    const shadingManual = floorBasedState?.facadeState.shadingManual ?? defaults.facadeState.shadingManual;
    const shadingAutomaticRectangle =
        floorBasedState?.facadeState.shadingAutomaticRectangle ?? defaults.facadeState.shadingAutomaticRectangle;
    const shadingAutomaticRectangleReference =
        floorBasedState?.facadeState.shadingAutomaticRectangleReference ?? defaults.facadeState.shadingAutomaticRectangleReference;
    const shadingAutomaticLateralVerticalShade =
        floorBasedState?.facadeState.shadingAutomaticLateralVerticalShade ?? defaults.facadeState.shadingAutomaticLateralVerticalShade;
    const shadingMode = shadingManual ? 'manual' : 'automatic';

    // =====================================
    // USE EFFECTS
    // =====================================

    useEffect(() => {
        if (!hasPvPanels) return;
        if (!floorBasedState) return;

        const floorHeightMin = floorBasedState.floorHeight?.minValue;
        const floorHeightValue = floorBasedState.floorHeight?.value;
        if (floorHeightValue === undefined || floorHeightValue < floorHeightMin) {
            updateFloorBased({
                floorHeight: { value: floorHeightMin },
                facadeState: {
                    verticalHeight: { value: floorHeightMin },
                },
            });
        }
    }, [hasPvPanels, floorBasedState, updateFloorBased]);

    const handleOpenLayerTree = (componentType: FacadeComponentType) => {
        addMixedLayerToCollection(); // Make sure Mixer is in the layerTree.
        setLayerTreeRefresh((k) => k + 1); // Force refresh LayerTree to reflect changes in layer collection.
        setSelectedComponentTypeForLayer(componentType);
        setLayerDialogOpen(true);
    };

    const handleLayerClick = (layerId: string) => {
        if (selectedComponentTypeForLayer === null) return;
        const grid = getGrid();
        if (!grid) return;
        const layer = grid.searchLayerByUID(layerId);
        if (!layer) return;

        updateFloorBased({
            facadeState: {
                componentLayerIds: {
                    ...componentLayerIds,
                    [selectedComponentTypeForLayer]: layer.getUID(),
                },
            },
        });
        setLayerDialogOpen(false);
    };

    // =====================================
    // ACTUAL HTML
    // =====================================

    return (
        <>
            <SidePanelDialog
                open={layerDialogOpen}
                onClose={() => setLayerDialogOpen(false)}
                side="right"
                title="Layer Linked to Component"
            >
                <Box sx={{ overflow: 'auto' }}>
                    <LayerTree key={layerTreeRefresh} onClick={handleLayerClick} />
                </Box>
            </SidePanelDialog>

            <CollapsibleGroupLabel label="Components" open={componentSectionOpen} onToggle={onToggle} />
            {componentSectionOpen && !hideFacadesEnabled && (
                <>
                    {/* CloseToDS Warning for Full Site */}
                    {isFullSite && (
                        <Stack direction="row" sx={{ color: 'error.main', minWidth: 0, mt: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: 0 }}>
                                Warning: In Full Site, components proximity filtering can be unreliable because sensor points may be missing
                                near analysis-area boundaries and after facade-facing filtering.
                            </Typography>
                        </Stack>
                    )}

                    {/* Component Type */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 1,
                            width: '100%',
                            minWidth: 0,
                        }}
                    >
                        <Typography sx={tinyLabelSx}>Type</Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            <Select
                                data-testid="ds-facade-component-select"
                                size="small"
                                multiple
                                value={componentTypes}
                                renderValue={(selected) =>
                                    (selected as FacadeComponentType[])
                                        .map((value) => {
                                            if (value === FacadeComponentType.PV_PANELS) return 'PvPanels';
                                            if (value === FacadeComponentType.SHADING) return 'Shading';
                                            if (value === FacadeComponentType.BALCONIES) return 'Balconies';
                                            if (value === FacadeComponentType.GREEN) return 'Green';
                                            return '';
                                        })
                                        .filter(Boolean)
                                        .join(' + ') || 'Nothing'
                                }
                                onChange={(e) => {
                                    const selectedComponentTypes = (e.target.value as number[])
                                        .map((value) => Number(value) as FacadeComponentType)
                                        .filter((value) =>
                                            [
                                                FacadeComponentType.PV_PANELS,
                                                FacadeComponentType.SHADING,
                                                FacadeComponentType.BALCONIES,
                                                FacadeComponentType.GREEN,
                                            ].includes(value)
                                        );
                                    updateFloorBased({
                                        facadeState: {
                                            componentTypes: selectedComponentTypes,
                                        },
                                    });

                                    const shouldAutoAssignMonthly =
                                        selectedComponentTypes.includes(FacadeComponentType.PV_PANELS) ||
                                        selectedComponentTypes.includes(FacadeComponentType.SHADING);

                                    if (shouldAutoAssignMonthly) {
                                        const grid = getGrid();
                                        if (!grid) return;

                                        const sim4DLayers = grid.findLayersByKind(SIM_SUN4D);
                                        const monthlyLayers = sim4DLayers.filter((l: Layer) => l.getFrameCollection().length === 12);
                                        if (!monthlyLayers.length) {
                                            showNotification(
                                                "Monthly Sun layer not found. Can't set component default months.",
                                                NotificationType.Error
                                            );
                                            return;
                                        }

                                        const pvLayer = monthlyLayers[0];
                                        const shadingLayer = monthlyLayers[1] ?? monthlyLayers[0];
                                        const nextComponentLayerIds = { ...componentLayerIds };

                                        // Applies the correct PV Panel frame (January):
                                        if (selectedComponentTypes.includes(FacadeComponentType.PV_PANELS)) {
                                            applyLayerFrameSelection(pvLayer.getUID(), 0);
                                            nextComponentLayerIds[FacadeComponentType.PV_PANELS] = pvLayer.getUID();
                                        }

                                        // Applies the correct Shading frame (July):
                                        if (selectedComponentTypes.includes(FacadeComponentType.SHADING)) {
                                            applyLayerFrameSelection(shadingLayer.getUID(), 6);
                                            nextComponentLayerIds[FacadeComponentType.SHADING] = shadingLayer.getUID();
                                        }

                                        updateFloorBased({
                                            facadeState: {
                                                componentLayerIds: nextComponentLayerIds,
                                            },
                                        });
                                    }
                                }}
                                sx={{
                                    ...tinySelectSx,
                                    flex: hasComponents ? '2 1 0' : '1 1 0',
                                }}
                            >
                                <MenuItem data-testid="ds-component-pv-panels" value={FacadeComponentType.PV_PANELS}>
                                    PvPanels
                                </MenuItem>
                                <MenuItem data-testid="ds-component-shading" value={FacadeComponentType.SHADING}>
                                    Shading
                                </MenuItem>
                                <MenuItem data-testid="ds-component-balconies" value={FacadeComponentType.BALCONIES}>
                                    Balconies
                                </MenuItem>
                                <MenuItem data-testid="ds-component-green" value={FacadeComponentType.GREEN}>
                                    Green
                                </MenuItem>
                            </Select>

                            {hasComponents && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                        flex: '1 1 0',
                                        minWidth: 0,
                                    }}
                                >
                                    <Checkbox
                                        data-testid="ds-component-link-checkbox"
                                        size="small"
                                        checked={componentLink}
                                        onChange={(e) =>
                                            updateFloorBased({
                                                facadeState: {
                                                    componentLink: e.target.checked,
                                                },
                                            })
                                        }
                                        sx={{
                                            padding: 0,
                                            '& .MuiSvgIcon-root': { fontSize: '16px' },
                                        }}
                                    />
                                    <Typography variant="body2">Data-Presence</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Component Direction for Unlinked Mode */}
                    {hasComponents && !componentLink && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mt: 1,
                                width: '100%',
                                minWidth: 0,
                            }}
                        >
                            <Typography sx={tinyLabelSx}>Direction</Typography>
                            <Select
                                size="small"
                                value={componentDirectionMode}
                                onChange={(e) =>
                                    updateFloorBased({
                                        facadeState: {
                                            componentDirectionMode: e.target.value as FacadeDirectionMode,
                                        },
                                    })
                                }
                                sx={tinySelectSx}
                            >
                                <MenuItem value={FacadeDirectionMode.FLOOR_AVERAGE}>Floor Average</MenuItem>
                                <MenuItem value={FacadeDirectionMode.VOLUME_AVERAGE}>Volume Average</MenuItem>
                            </Select>
                        </Box>
                    )}

                    {/** Components Layers */}
                    {hasComponents && (
                        <>
                            <GroupLabel label="Component Layers" />
                            {componentTypes.map((componentType) => {
                                const layerId = componentLayerIds[componentType];
                                const layerName = layerId ? getGrid()?.searchLayerByUID(layerId)?.name : undefined;
                                const label =
                                    componentType === FacadeComponentType.PV_PANELS
                                        ? 'PvPanels'
                                        : componentType === FacadeComponentType.BALCONIES
                                          ? 'Balconies'
                                          : componentType === FacadeComponentType.GREEN
                                            ? 'Green'
                                            : componentType === FacadeComponentType.SHADING
                                              ? 'Shading'
                                              : 'Component';
                                return (
                                    <Box
                                        key={componentType}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mt: 1,
                                            width: '100%',
                                            minWidth: 0,
                                        }}
                                    >
                                        <Typography sx={{ ...tinyLabelSx, flex: '0 0 56px' }}>{label}</Typography>
                                        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                                            <Button variant="outlined" onClick={() => handleOpenLayerTree(componentType)} fullWidth>
                                                {layerName ?? 'Select Layer'}
                                            </Button>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </>
                    )}

                    {/* PV Panels */}
                    {hasPvPanels && (
                        <>
                            {/* PV Panels Settings */}
                            <GroupLabel label="PV Panels Settings" />

                            {/* Coverage */}
                            <Box sx={{ ...sliderRowSx, mt: 1 }}>
                                <Typography variant="body2">Coverage</Typography>
                                <Select
                                    data-testid="ds-vertical-coverage-mode-select"
                                    size="small"
                                    value={verticalCoverageMode}
                                    renderValue={(value) => {
                                        switch (value as VerticalFacadeCoverageMode) {
                                            case VerticalFacadeCoverageMode.FACING:
                                                return 'Facing facades';
                                            case VerticalFacadeCoverageMode.FACING_AND_ADJACENT:
                                                return 'Facing & adjacent';
                                            case VerticalFacadeCoverageMode.ALL:
                                                return 'All facades';
                                            default:
                                                return '';
                                        }
                                    }}
                                    onChange={(e) =>
                                        updateFloorBased({
                                            facadeState: {
                                                verticalCoverageMode: e.target.value as VerticalFacadeCoverageMode,
                                            },
                                        })
                                    }
                                    sx={tinySelectSx}
                                >
                                    <MenuItem value={VerticalFacadeCoverageMode.FACING}>Facing facades</MenuItem>
                                    <MenuItem value={VerticalFacadeCoverageMode.FACING_AND_ADJACENT}>Facing & adjacent facades</MenuItem>
                                    <MenuItem value={VerticalFacadeCoverageMode.ALL}>All facades</MenuItem>
                                </Select>
                            </Box>

                            {/* Corner / Closure Options */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    mt: 1,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Checkbox
                                        data-testid="ds-vertical-connect-corners-checkbox"
                                        size="small"
                                        checked={verticalConnectCorners}
                                        onChange={(e) =>
                                            updateFloorBased({
                                                facadeState: {
                                                    verticalConnectCorners: e.target.checked,
                                                },
                                            })
                                        }
                                        sx={{
                                            padding: 0,
                                            '& .MuiSvgIcon-root': { fontSize: '16px' },
                                        }}
                                    />
                                    <Typography variant="body2">Connect corners</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Checkbox
                                        data-testid="ds-vertical-closed-checkbox"
                                        size="small"
                                        checked={verticalClosed}
                                        onChange={(e) =>
                                            updateFloorBased({
                                                facadeState: {
                                                    verticalClosed: e.target.checked,
                                                },
                                            })
                                        }
                                        sx={{
                                            padding: 0,
                                            '& .MuiSvgIcon-root': { fontSize: '16px' },
                                        }}
                                    />
                                    <Typography variant="body2">Closed</Typography>
                                </Box>
                            </Box>

                            {/* Panel Height (a) */}
                            <Box sx={sliderRowSx}>
                                <Typography variant="body2" sx={optionalSliderLabelSx}>
                                    Height a
                                </Typography>

                                <Slider
                                    {...sliderInteractionProps}
                                    data-testid="ds-vertical-height-slider"
                                    className="panel-slider"
                                    size="small"
                                    value={verticalHeight}
                                    min={0}
                                    max={floorHeight + floorThickness}
                                    step={0.1}
                                    valueLabelDisplay="on"
                                    valueLabelFormat={formatOneDecimal}
                                    onChange={(_, value) =>
                                        updateFloorBased({
                                            facadeState: {
                                                verticalHeight: { value },
                                            },
                                        })
                                    }
                                    sx={sliderSx}
                                />
                                <Typography variant="body2" sx={sliderUnitSx}>
                                    [m]
                                </Typography>
                            </Box>

                            {/* Panel Length (L) */}
                            <Box sx={sliderRowSx}>
                                <Typography variant="body2" sx={optionalSliderLabelSx}>
                                    Length L
                                </Typography>

                                <Slider
                                    {...sliderInteractionProps}
                                    data-testid="ds-vertical-length-slider"
                                    className="panel-slider"
                                    size="small"
                                    value={verticalLength}
                                    min={0.5}
                                    max={5}
                                    step={0.05}
                                    valueLabelDisplay="on"
                                    valueLabelFormat={formatOneDecimal}
                                    onChange={(_, value) =>
                                        updateFloorBased({
                                            facadeState: {
                                                verticalLength: { value },
                                            },
                                        })
                                    }
                                    sx={sliderSx}
                                />
                                <Typography variant="body2" sx={sliderUnitSx}>
                                    [m]
                                </Typography>
                            </Box>

                            {/* Panel Offset */}
                            <Box sx={sliderRowSx}>
                                <Typography variant="body2" sx={optionalSliderLabelSx}>
                                    Offset
                                </Typography>

                                <Slider
                                    {...sliderInteractionProps}
                                    data-testid="ds-pv-offset-slider"
                                    className="panel-slider"
                                    size="small"
                                    value={pvOffset}
                                    min={defaults.facadeState.pvOffset.minValue}
                                    max={defaults.facadeState.pvOffset.maxValue}
                                    step={0.1}
                                    valueLabelDisplay="on"
                                    valueLabelFormat={formatOneDecimal}
                                    onChange={(_, value) =>
                                        updateFloorBased({
                                            facadeState: {
                                                pvOffset: { value },
                                            },
                                        })
                                    }
                                    sx={sliderSx}
                                />
                                <Typography variant="body2" sx={sliderUnitSx}>
                                    [m]
                                </Typography>
                            </Box>

                            {/* Panel Angle α [deg] */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    width: '100%',
                                    minWidth: 0,
                                }}
                            >
                                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                    Angle α
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Checkbox
                                        data-testid="ds-vertical-custom-angle-checkbox"
                                        size="small"
                                        checked={verticalCustomAngle}
                                        onChange={(e) =>
                                            updateFloorBased({
                                                facadeState: {
                                                    verticalCustomAngle: e.target.checked,
                                                },
                                            })
                                        }
                                        sx={{
                                            padding: 0,
                                            ml: 0.5,
                                            '& .MuiSvgIcon-root': { fontSize: '14px' },
                                        }}
                                    />
                                    <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Manual</Typography>
                                </Box>

                                <Slider
                                    {...sliderInteractionProps}
                                    data-testid="ds-vertical-angle-slider"
                                    className="panel-slider"
                                    size="small"
                                    value={verticalAngle}
                                    min={defaults.facadeState.verticalAngle.minValue}
                                    max={defaults.facadeState.verticalAngle.maxValue}
                                    step={1}
                                    valueLabelDisplay="on"
                                    valueLabelFormat={formatOneDecimal}
                                    disabled={!verticalCustomAngle}
                                    onChange={(_, value) =>
                                        updateFloorBased({
                                            facadeState: {
                                                verticalAngle: { value },
                                            },
                                        })
                                    }
                                    sx={{ flex: '1 1 0', minWidth: 0 }}
                                />
                                <Typography variant="body2" sx={{ ml: 0.5, textAlign: 'right', whiteSpace: 'nowrap', flex: '0 0 auto' }}>
                                    [°]
                                </Typography>
                            </Box>

                            {/* Panel Height (b) */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                    Height b (m)
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 500,
                                        minWidth: 48,
                                        textAlign: 'right',
                                    }}
                                >
                                    {verticalHeightB !== undefined ? verticalHeightB.toFixed(2) : '–'}
                                </Typography>
                            </Box>
                        </>
                    )}

                    {/* Balcony Settings */}
                    {hasBalconies && (
                        <>
                            <GroupLabel label="Balcony Settings" />

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    mt: 1,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Checkbox
                                        data-testid="ds-balcony-connect-corners-checkbox"
                                        size="small"
                                        checked={balconyConnectCorners}
                                        onChange={(e) =>
                                            updateFloorBased({
                                                facadeState: {
                                                    balconyConnectCorners: e.target.checked,
                                                },
                                            })
                                        }
                                        sx={{
                                            padding: 0,
                                            '& .MuiSvgIcon-root': { fontSize: '16px' },
                                        }}
                                    />
                                    <Typography variant="body2">Connect corners</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Checkbox
                                        data-testid="ds-balcony-only-on-openings-checkbox"
                                        size="small"
                                        checked={balconyOnlyOnOpenings}
                                        onChange={(e) =>
                                            updateFloorBased({
                                                facadeState: {
                                                    balconyOnlyOnOpenings: e.target.checked,
                                                },
                                            })
                                        }
                                        sx={{
                                            padding: 0,
                                            '& .MuiSvgIcon-root': { fontSize: '16px' },
                                        }}
                                    />
                                    <Typography variant="body2">Only on Openings</Typography>
                                </Box>
                            </Box>

                            <Box sx={sliderRowSx}>
                                <Typography variant="body2" sx={optionalSliderLabelSx}>
                                    Length L
                                </Typography>

                                <Slider
                                    {...sliderInteractionProps}
                                    data-testid="ds-balcony-length-slider"
                                    className="panel-slider"
                                    size="small"
                                    value={balconyLength}
                                    min={0.5}
                                    max={5}
                                    step={0.05}
                                    valueLabelDisplay="on"
                                    valueLabelFormat={formatOneDecimal}
                                    onChange={(_, value) =>
                                        updateFloorBased({
                                            facadeState: {
                                                balconyLength: { value },
                                            },
                                        })
                                    }
                                    sx={sliderSx}
                                />
                                <Typography variant="body2" sx={sliderUnitSx}>
                                    [m]
                                </Typography>
                            </Box>
                        </>
                    )}

                    {/* Shading Settings */}
                    {hasShading && (
                        <>
                            <GroupLabel label="Shading Settings" />

                            <Box sx={{ ...sliderRowSx, mt: 1 }}>
                                <Typography variant="body2" sx={optionalSliderLabelSx}>
                                    Mode
                                </Typography>
                                <Select
                                    data-testid="ds-shading-mode-select"
                                    size="small"
                                    value={shadingMode}
                                    onChange={(e) =>
                                        updateFloorBased({
                                            facadeState: {
                                                shadingManual: e.target.value === 'manual',
                                            },
                                        })
                                    }
                                    sx={tinySelectSx}
                                >
                                    <MenuItem value="manual">Manual</MenuItem>
                                    <MenuItem value="automatic">Automatic</MenuItem>
                                </Select>
                            </Box>

                            {shadingManual && (
                                <>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            mt: 1,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Checkbox
                                                data-testid="ds-shading-connect-corners-checkbox"
                                                size="small"
                                                checked={shadingConnectCorners}
                                                onChange={(e) =>
                                                    updateFloorBased({
                                                        facadeState: {
                                                            shadingConnectCorners: e.target.checked,
                                                        },
                                                    })
                                                }
                                                sx={{
                                                    padding: 0,
                                                    '& .MuiSvgIcon-root': { fontSize: '16px' },
                                                }}
                                            />
                                            <Typography variant="body2">Connected</Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ ...sliderRowSx, mt: 1 }}>
                                        <Typography variant="body2" sx={optionalSliderLabelSx}>
                                            Depth
                                        </Typography>
                                        <Slider
                                            {...sliderInteractionProps}
                                            data-testid="ds-shading-depth-slider"
                                            className="panel-slider"
                                            size="small"
                                            value={shadingDepth}
                                            min={defaults.facadeState.shadingDepth.minValue}
                                            max={defaults.facadeState.shadingDepth.maxValue}
                                            step={0.1}
                                            valueLabelDisplay="on"
                                            valueLabelFormat={formatOneDecimal}
                                            onChange={(_, value) =>
                                                updateFloorBased({
                                                    facadeState: {
                                                        shadingDepth: { value },
                                                    },
                                                })
                                            }
                                            sx={sliderSx}
                                        />
                                        <Typography variant="body2" sx={sliderUnitSx}>
                                            [m]
                                        </Typography>
                                    </Box>
                                </>
                            )}

                            {!shadingManual && (
                                <>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            mt: 1,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Checkbox
                                                data-testid="ds-shading-lateral-vertical-shade-checkbox"
                                                size="small"
                                                checked={shadingAutomaticLateralVerticalShade}
                                                onChange={(e) =>
                                                    updateFloorBased({
                                                        facadeState: {
                                                            shadingAutomaticLateralVerticalShade: e.target.checked,
                                                        },
                                                    })
                                                }
                                                sx={{
                                                    padding: 0,
                                                    '& .MuiSvgIcon-root': { fontSize: '16px' },
                                                }}
                                            />
                                            <Typography variant="body2">Lateral vertical shade</Typography>
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            mt: 1,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Checkbox
                                                data-testid="ds-shading-rectangle-checkbox"
                                                size="small"
                                                checked={shadingAutomaticRectangle}
                                                onChange={(e) =>
                                                    updateFloorBased({
                                                        facadeState: {
                                                            shadingAutomaticRectangle: e.target.checked,
                                                        },
                                                    })
                                                }
                                                sx={{
                                                    padding: 0,
                                                    '& .MuiSvgIcon-root': { fontSize: '16px' },
                                                }}
                                            />
                                            <Typography variant="body2">Rectangle</Typography>
                                        </Box>
                                    </Box>

                                    {shadingAutomaticRectangle && (
                                        <Box sx={{ ...sliderRowSx, mt: 1 }}>
                                            <Typography variant="body2" sx={optionalSliderLabelSx}>
                                                Side
                                            </Typography>
                                            <Select
                                                data-testid="ds-shading-rectangle-reference-select"
                                                size="small"
                                                value={shadingAutomaticRectangleReference}
                                                onChange={(e) =>
                                                    updateFloorBased({
                                                        facadeState: {
                                                            shadingAutomaticRectangleReference: Number(
                                                                e.target.value
                                                            ) as ShadingAutomaticRectangleReference,
                                                        },
                                                    })
                                                }
                                                sx={tinySelectSx}
                                            >
                                                <MenuItem value={ShadingAutomaticRectangleReference.LONGER}>Longer</MenuItem>
                                                <MenuItem value={ShadingAutomaticRectangleReference.SHORTER}>Shorter</MenuItem>
                                            </Select>
                                        </Box>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {/* Green Settings */}
                    {hasGreen && (
                        <>
                            <GroupLabel label="Green Settings" />
                            <Box sx={{ ...sliderRowSx, mt: 1 }}>
                                <Typography variant="body2" sx={optionalSliderLabelSx}>
                                    Depth
                                </Typography>
                                <Slider
                                    {...sliderInteractionProps}
                                    data-testid="ds-green-depth-slider"
                                    className="panel-slider"
                                    size="small"
                                    value={greenDepth}
                                    min={0.1}
                                    max={4}
                                    step={0.1}
                                    valueLabelDisplay="on"
                                    valueLabelFormat={formatOneDecimal}
                                    onChange={(_, value) =>
                                        updateFloorBased({
                                            facadeState: {
                                                greenDepth: { value },
                                            },
                                        })
                                    }
                                    sx={sliderSx}
                                />
                                <Typography variant="body2" sx={sliderUnitSx}>
                                    [m]
                                </Typography>
                            </Box>
                        </>
                    )}
                </>
            )}
        </>
    );
};
